import { useQueryClient } from 'react-query';
import { useApiQuery, useApiMutation, ApiError, MutationOptions } from '../../../api/useApi';

// ─── Types ──────────────────────────────────────

export type AlertSeverity = 'info' | 'warning' | 'urgent';
export type AlertStatus = 'pending' | 'acknowledged' | 'resolved';
export type AlertActionType = 'prolonger' | 'stopper' | 'traiter' | 'rouvrir';

export type AlertActionLog = {
    id: number;
    action: string;
    metadata: Record<string, any> | null;
    user: { id: number; name: string };
    created_at: string;
};

export type Alert = {
    id: number;
    campaign_id: number;
    facility_id: number;
    action_launch_id: number | null;
    action_id: number | null;
    /** Passages du même noeud de flow regroupés dans cette alerte. */
    occurrences: number;
    last_raised_at: string | null;
    severity: AlertSeverity;
    message: string;
    status: AlertStatus;
    resolved_at: string | null;
    resolved_by: { id: number; name: string } | null;
    resolution_action: string | null;
    campaign?: { id: number; name: string; status: string };
    facility?: { id: number; name: string };
    action_logs?: AlertActionLog[];
    created_at: string;
    updated_at: string;
};

export type AlertsResponse = {
    data: Alert[];
    meta: {
        current_page: number;
        last_page: number;
        total: number;
    };
};

export type DashboardAlertsResponse = {
    total: number;
    count_by_severity: {
        urgent: number;
        warning: number;
        info: number;
    };
    alerts: Alert[];
};

export type AlertActionPayload = {
    id: number;
    action: AlertActionType;
    extension_days?: number;
};

// ─── Queries ────────────────────────────────────

export function useAlerts(params?: {
    facility_id?: number;
    campaign_id?: number;
    severity?: string;
    status?: string;
    page?: number;
}) {
    const searchParams = new URLSearchParams();
    if (params?.facility_id) searchParams.set('facility_id', String(params.facility_id));
    if (params?.campaign_id) searchParams.set('campaign_id', String(params.campaign_id));
    if (params?.severity) searchParams.set('severity', params.severity);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', String(params.page));
    const url = `/alerts?${searchParams}`;

    return useApiQuery<AlertsResponse>(url);
}

export function useDashboardAlerts() {
    return useApiQuery<DashboardAlertsResponse>('/alerts/dashboard');
}

export function useAlert(id: number | null) {
    return useApiQuery<Alert>(
        id ? `/alerts/${id}` : '',
        {},
        { enabled: !!id }
    );
}

export function useCampaignAlerts(campaignId: number | string | null) {
    return useApiQuery<Alert[]>(
        campaignId ? `/campaigns/${campaignId}/alerts` : '',
        {},
        { enabled: !!campaignId }
    );
}

// ─── Mutations ──────────────────────────────────

export function useAlertAction(options: MutationOptions<Alert, ApiError, AlertActionPayload> = {}) {
    const queryClient = useQueryClient();
    return useApiMutation<Alert, ApiError, AlertActionPayload>(
        'post',
        (vars) => `/alerts/${vars.id}/action`,
        {
            ...options,
            onSuccess: (data, variables, context) => {
                // Predicate : les listes paginées/filtrées ont des query strings
                // ('/alerts?...') qu'un match exact de clé ne couvrirait pas.
                queryClient.invalidateQueries({ predicate: q => String(q.queryKey[0] ?? '').includes('/alerts') });
                queryClient.invalidateQueries({ predicate: q => String(q.queryKey[0] ?? '').includes('/campaigns') });
                queryClient.invalidateQueries({ predicate: q => String(q.queryKey[0] ?? '').includes('/facilities') });
                options.onSuccess?.(data, variables, context);
            },
        }
    );
}

export function useAcknowledgeAlert(options: MutationOptions<any, ApiError, { id: number }> = {}) {
    const queryClient = useQueryClient();
    return useApiMutation<any, ApiError, { id: number }>(
        'post',
        (vars) => `/alerts/${vars.id}/acknowledge`,
        {
            ...options,
            onSuccess: (data, variables, context) => {
                queryClient.invalidateQueries({ predicate: q => String(q.queryKey[0] ?? '').includes('/alerts') });
                options.onSuccess?.(data, variables, context);
            },
        }
    );
}
